/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef } from 'react';

import { debounce } from 'lodash';

import { breakpoints } from '@coursera/cds-core';

import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';

type Props = {
  headerRef: React.RefObject<HTMLSpanElement>;
  // the space between the page header and the chat container in pixels
  topOffset?: number;
};

const CHAT_CONTAINER_DEFAULT_TOP_POSITION = 105;
const CHAT_CONTAINER_BOTTOM_OFFSET = 16;

const styles = {
  chatStyles: css`
    border-radius: var(--cds-border-radius-200);
    position: fixed;
    top: 160px;
    bottom: var(--cds-spacing-200);
    right: var(--cds-spacing-200);
    min-width: 300px;
    width: 100%;
    max-width: 432px;
    z-index: 3001;
    box-shadow: 0 0 28px -6px rgb(0 0 0 / 16%), 0 0 28px -12px rgb(0 0 0 / 16%);

    ${breakpoints.down('sm')} {
      left: 0;
      max-width: 100%;
      box-shadow: none;
    }
  `,
  childContainer: css`
    position: relative;
    height: 100%;

    ${breakpoints.down('sm')} {
      max-width: 100%;
      margin: 0;
      height: 100%;
    }
  `,
};

const CoachChatContainer = ({ headerRef, topOffset = 16, children }: React.PropsWithChildren<Props>) => {
  const { coachOpen } = useCoachState();
  const chatRef = useRef<HTMLDivElement | null>(null);
  const documentOverflowRef = useRef<string | null>(null);

  useEffect(() => {
    const chatWindowFullScreenBreakpoint = breakpoints.values.md;

    if (!coachOpen || !chatRef.current || typeof window === 'undefined') {
      return undefined;
    }

    if (documentOverflowRef.current === null) {
      documentOverflowRef.current = document.body.style.overflow || '';
    }

    const updatePosition = () => {
      if (!chatRef.current) return;

      if (window.innerWidth < chatWindowFullScreenBreakpoint) {
        // prevent the page from scrolling when the chat window is full screen
        document.body.style.overflow = 'hidden';

        chatRef.current.style.top = '0px';
        chatRef.current.style.bottom = '0px';
      } else {
        // allow the page to scroll when the chat window is not full screen
        document.body.style.overflow = documentOverflowRef.current || 'visible';

        const headerHeight =
          (headerRef.current?.getBoundingClientRect()?.height || CHAT_CONTAINER_DEFAULT_TOP_POSITION) + topOffset;
        chatRef.current.style.top = `${headerHeight}px`;
        chatRef.current.style.bottom = `${CHAT_CONTAINER_BOTTOM_OFFSET}px`;
      }
    };

    updatePosition();

    const updatePositionDebounce = debounce(() => updatePosition(), 100);
    window.addEventListener('resize', updatePositionDebounce);

    return () => {
      window.removeEventListener('resize', updatePositionDebounce);
      updatePositionDebounce.cancel();

      document.body.style.overflow = documentOverflowRef.current || 'visible';
    };
  }, [headerRef, coachOpen, topOffset]);

  return (
    <div ref={chatRef} css={styles.chatStyles}>
      <div css={styles.childContainer}>{children}</div>
    </div>
  );
};

export default CoachChatContainer;
